import type { SchemaField } from '@cli-types/core-engine/schema.js'
import { quotePropertyName } from '@cli-utils/text.js'
import { isUserRelationshipTarget } from '@core-engine/schema/system-relationships/is-user-relationship-target.js'

export function getZodType(field: SchemaField): string {
  const label = field.label || field.name
  if (field.name === 'slug' && field.hidden) return 'z.string().optional()'

  switch (field.type) {
    case 'uuid':
    case 'serial':
      return field.required
        ? `z.string().uuid('${label} must be a valid UUID')`
        : `z.string().uuid('${label} must be a valid UUID').optional()`
    case 'number':
    case 'decimal':
      return field.required
        ? `z.number({ message: '${label} is required' })`
        : 'z.number().nullable().optional()'
    case 'boolean':
      return 'z.boolean()'
    case 'email': {
      const base = field.required
        ? `z.string().min(1, '${label} is required').email('Please enter a valid email address')`
        : `z.string().optional().refine((val) => !val || val.trim() === '' || z.string().email().safeParse(val).success, { message: 'Please enter a valid email address' })`
      return field.length ? `${base}.max(${field.length})` : base
    }
    case 'string':
    case 'varchar':
    case 'text':
    case 'markdown':
    case 'richtext': {
      if (field.name.toLowerCase().includes('email')) {
        const base = field.required
          ? `z.string().min(1, '${label} is required').email('Please enter a valid email address')`
          : `z.string().email('Please enter a valid email address').optional()`
        return field.length ? `${base}.max(${field.length})` : base
      }
      const base = field.required ? `z.string().min(1, '${label} is required')` : 'z.string()'
      return field.length ? `${base}.max(${field.length})` : base
    }
    case 'date':
      if (!field.required) {
        return 'z.string().transform(val => val === "" ? undefined : val).optional()'
      }
      return `z.string().min(1, '${label} is required')`
    case 'timestamp':
      return field.required ? `z.string().min(1, '${label} is required')` : 'z.string().optional()'
    case 'image':
    case 'video':
    case 'media': {
      if (!field.required) {
        return 'z.string().transform(val => val === "" ? undefined : val).optional()'
      }
      return `z.string().min(1, 'Please select ${label.toLowerCase()} from the media gallery')`
    }
    case 'gallery':
      return field.required
        ? `z.array(z.string()).min(1, '${label} must have at least one item')`
        : 'z.array(z.string()).optional()'
    case 'list':
      if (field.fields && field.fields.length > 0) {
        const nested = field.fields
          .flatMap((nf) => {
            const defs: string[] = []
            const zodType = getZodType(nf)
            const alreadyOptional = zodType.includes('.optional()')
            let def = `    ${quotePropertyName(nf.name)}: ${zodType}`
            if (!nf.required && !alreadyOptional) def += '.optional()'
            defs.push(def)
            if (nf.hasIcon)
              defs.push(`    ${quotePropertyName(`${nf.name}Icon`)}: z.string().optional()`)
            return defs
          })
          .join(',\n')
        const obj = `z.object({\n${nested}\n  })`
        const arr = field.maxItems ? `z.array(${obj}).max(${field.maxItems})` : `z.array(${obj})`
        return field.required
          ? `${arr}.min(1, '${label} must have at least one item')`
          : `${arr}.optional()`
      }
      return field.required
        ? `z.array(z.string()).min(1, '${label} must have at least one item')`
        : 'z.array(z.string()).optional()'
    case 'select': {
      if (field.multiple) {
        if (field.creatable) {
          return field.required
            ? `z.array(z.string()).min(1, '${label} is required')`
            : 'z.array(z.string()).optional()'
        }
        if (field.options && field.options.length > 0) {
          const values = field.options.map((o) => `'${o.value}'`).join(', ')
          const arr = `[${values}] as const`
          const baseArraySchema = field.required
            ? `z.array(z.string()).min(1, '${label} is required')`
            : 'z.array(z.string())'
          const arraySchema = `${baseArraySchema}.refine((values) => values.every((value) => (${arr} as readonly string[]).includes(value)), { message: 'Invalid ${label}' })`
          return field.required ? arraySchema : `${arraySchema}.optional()`
        }
        return field.required
          ? `z.array(z.string()).min(1, '${label} is required')`
          : 'z.array(z.string()).optional()'
      }
      if (field.creatable) {
        return field.required
          ? `z.string().min(1, '${label} is required')`
          : 'z.string().optional()'
      }
      if (field.options && field.options.length > 0) {
        const values = field.options.map((o) => `'${o.value}'`).join(', ')
        const arr = `[${values}] as const`
        return field.required
          ? `z.string().min(1, '${label} is required').refine((val) => (${arr} as readonly string[]).includes(val), { message: 'Invalid ${label}' })`
          : `z.string().refine((val) => !val || (${arr} as readonly string[]).includes(val), { message: 'Invalid ${label}' }).optional()`
      }
      return field.required ? `z.string().min(1, '${label} is required')` : 'z.string().optional()'
    }
    case 'icon':
      return field.required ? `z.string().min(1, '${label} is required')` : 'z.string().optional()'
    case 'relationship':
      if (field.multiple) {
        if (isUserRelationshipTarget(field.relationship)) {
          return field.required
            ? `z.array(z.string()).min(1, '${label} is required')`
            : 'z.array(z.string()).optional()'
        }
        return field.required
          ? `z.array(z.string().uuid()).min(1, '${label} is required')`
          : 'z.array(z.string().uuid()).optional()'
      }
      return field.required ? `z.string().min(1, '${label} is required')` : 'z.string().optional()'
    case 'group':
      if (field.fields && field.fields.length > 0) {
        const nested = field.fields
          .map((nf) => {
            const zodType = getZodType(nf)
            const alreadyOptional = zodType.includes('.optional()')
            let def = `    ${quotePropertyName(nf.name)}: ${zodType}`
            if (!nf.required && !alreadyOptional) def += '.optional()'
            return def
          })
          .join(',\n')
        return field.required
          ? `z.object({\n${nested}\n  })`
          : `z.object({\n${nested}\n  }).optional()`
      }
      return 'z.record(z.unknown()).optional()'
    default:
      return field.required ? `z.string().min(1, '${label} is required')` : 'z.string().optional()'
  }
}
